import { datadogRum } from '@datadog/browser-rum';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { formatDistance } from 'date-fns';
import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';

import { APP_ROOT, STORE_ROOT } from 'utils/constants/urls';
import {
  IterableProduct,
  MembershipTypeOptions,
  Product,
  ProductBySlugType,
  ProductDataType,
  ProductsBySlugType,
  Variant,
} from 'utils/types';
import { TestPlanItemAttribute } from 'utils/hooks/useTestPlan/types';
import { MemberShipCategory } from './hooks/useProductsByCategory/membershipProductSkuMap';

const logError = (
  error: string,
  context: object | undefined,
  source: 'custom' | 'network' | 'source' | undefined = 'custom',
): void => {
  datadogRum.addError(error, context, source);
};

const formatDate = (date: string): string => {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const daysUntil = (date?: string): number | undefined => {
  if (!date) return;
  const then = new Date(date);
  const now = new Date();
  return Math.ceil((then.valueOf() - now.valueOf()) / (1000 * 3600 * 24));
};

const numericDate = (date: string): string => {
  return new Date(date).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
  });
};

const timeAgo = (date: string): string => {
  if (!date) return '';
  return (
    date &&
    formatDistance(new Date(date), new Date(), {
      addSuffix: true,
    })
  );
};

const formatPrice = (price: number): string => {
  if (price) return price.toFixed(2);
  return '0.00';
};

const scrollToId = (id: string): null | void => {
  if (typeof window === `undefined`) return null;
  const headerHeight = 86;
  const el = document.getElementById(id);

  if (!el) return;

  // add polyfill to support smooth scrolling
  smoothscroll.polyfill();

  // scroll to element and make room for header
  window.scrollBy({
    behavior: 'smooth',
    top: el.offsetTop - window.pageYOffset - headerHeight,
  });
};

export const loadScrollToAnchor = (): void => {
  try {
    const hash = window.location.hash && window.location.hash.substring(1);
    if (hash) scrollToId(hash);
  } catch (err) {
    logError((err as Error).message, {
      method: 'loadScrollToAnchor',
    });
  }
};

const useClickOff = (onClose: (e: any) => void) => {
  const wrapperEl = useRef<HTMLDivElement>(null);

  const handleClick = (e: any) => {
    if (!wrapperEl.current || !e.target) {
      return;
    }

    if (!wrapperEl.current.contains(e.target as Node)) {
      onClose(e);
    }
  };

  // Solves the issue of tapping away in iOS to trigger the click event on the document
  useEffect(() => {
    document.body.style.cursor = 'pointer';

    return () => {
      document.body.style.cursor = 'default';
    };
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return wrapperEl;
};

/**
 * Currently an open bug with body-scroll-lock on iPhone specifically iOS 12.x
 * if you touch at the bottom of the page when the browser controls ( bottom bar ) are hidden
 * you are still able to scroll the background
 *
 * when the controls down at the bottom are visible, the body-scroll-lock will work as intended
 *
 * the touchmove event isn't triggered at all down in the area of the bottom toolbar.
 * bug filed: https://bugs.webkit.org/show_bug.cgi?id=189586
 * github issue: https://github.com/willmcpo/body-scroll-lock/issues/82
 * */

const useDisableBodyScroll = (
  ref?: RefObject<HTMLDivElement>,
): RefObject<HTMLDivElement> => {
  const wrapperEl = ref || useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = wrapperEl.current;

    if (node) {
      disableBodyScroll(node, {
        reserveScrollBarGap: true,
      });
    }
    return () => {
      if (node) {
        enableBodyScroll(node);
      }
    };
  }, [wrapperEl]);

  return wrapperEl;
};

const useHasScrollableContent = (
  parentRef: RefObject<HTMLDivElement>,
): void | boolean => {
  const THRESHOLD = 20;
  const [hasScrollableContent, setHasScrollableContent] = useState(false);

  const handleScroll = (e: Event) => {
    if (!e.currentTarget) {
      return;
    }

    const target = e.currentTarget as HTMLDivElement;
    setHasScrollableContent(
      target.scrollHeight - target.scrollTop > target.clientHeight + THRESHOLD,
    );
  };

  useLayoutEffect(() => {
    if (!parentRef.current) {
      return;
    }

    parentRef.current.addEventListener('scroll', handleScroll);

    const { clientHeight, scrollHeight } = parentRef.current;

    if (scrollHeight > clientHeight + THRESHOLD) {
      setHasScrollableContent(true);
    }

    /* eslint-disable-next-line consistent-return */
    return () => {
      if (!parentRef.current) {
        return;
      }

      parentRef.current.removeEventListener('scroll', handleScroll);
    };
  }, [parentRef]);

  return hasScrollableContent;
};

const truncateString = (
  str: string,
  len: number,
  delimiter?: string,
  cap?: string,
): string => {
  if (str && str.length > len) {
    const lengthTrim = str.substr(0, len - 1);
    const endCap = cap || '...';
    const delim = delimiter || ' ';
    const finalString =
      lengthTrim.substr(
        0,
        Math.min(lengthTrim.length, lengthTrim.lastIndexOf(delim)),
      ) + endCap;
    return finalString;
  }

  return str;
};

const capitalize = (str: string): string => str[0].toUpperCase() + str.slice(1);

const formatForCurrency = (numberToFormat: number): string =>
  new Intl.NumberFormat('en-US', {
    currency: 'USD',
    minimumFractionDigits: 0,
    style: 'currency',
  }).format(numberToFormat);

const generateKey: (length?: number) => string = (length = 8) => {
  return Math.random().toString(16).substr(2, length);
};

const membershipIntakeUrl = (product_id: number): string => {
  const intakeUrl = new URL(`${APP_ROOT}/membership-intake`);
  const queryParams = new URLSearchParams();

  queryParams.append('variantId', `${product_id}`);
  intakeUrl.search = queryParams.toString();

  return intakeUrl.href;
};

const loginUrl = (redirect: string = window.location.href): string => {
  const url = new URL(`${STORE_ROOT}/login`);
  const queryParams = new URLSearchParams();

  queryParams.append('my_results_redirect', redirect);
  url.search = queryParams.toString();

  return url.href;
};

const getMembershipTypeFromProduct = (
  membershipProduct: number,
): MembershipTypeOptions => {
  let membershipType: MembershipTypeOptions;
  switch (membershipProduct) {
    case 374:
      membershipType = MembershipTypeOptions.current;
      break;
    case 504:
    default:
      membershipType = MembershipTypeOptions.control;
      break;
  }
  return membershipType;
};

const getProductsBySlug = (
  nodes: ProductBySlugType[],
): Omit<ProductsBySlugType, 'reviewData'> => {
  const productsBySlug: Omit<ProductsBySlugType, 'reviewData'> = {};
  nodes
    .filter((node) => node.contentfulProductInfo !== null)
    .forEach((node) => {
      const variant = node.variants?.filter((variant) => variant.is_master)[0];
      // @ts-ignore
      productsBySlug[node.slug] = {
        slug: node.slug,
        name: node?.contentfulProductInfo?.displayName || '',
        ...(node?.contentfulProductInfo?.productCardImage && {
          boxImage: node?.contentfulProductInfo?.productCardImage,
        }),
        ...(node.contentfulProductInfo.boxImage && {
          cartImage: node.contentfulProductInfo.boxImage,
        }),
        productId: +node.productId,
        variantId: variant.id,
        price: variant.price,
      };
    });
  return productsBySlug;
};

const filterUniqueByCategory = (
  aryOfProducts: ProductDataType[],
  category: string,
): ProductDataType[] => {
  const seen: { [key: number]: number } = {};
  return aryOfProducts.filter((x) => {
    if (category === x.category && !seen[x.productId]) {
      seen[x.productId] = 1;
      return true;
    } else {
      return false;
    }
  });
};

export const getProductsOrderedByPopularity = (
  products: ProductDataType[],
): ProductDataType[] => {
  return products.sort((a, b) => {
    if (a.popularityOrder && b.popularityOrder) {
      return a.popularityOrder - b.popularityOrder;
    }
    return 0;
  });
};

const useEffectAsyncWrapper: (cb: () => void) => Promise<void> = async (cb) => {
  try {
    cb();
  } catch (e: any) {
    logError(e, undefined, 'custom');
  }
};

const snakeToTitle = (str: string): string =>
  str.charAt(0).toUpperCase() +
  str
    .slice(1)
    .replace(/([-_][a-z])/g, (group) =>
      group.toUpperCase().replace('-', ' ').replace('_', ' '),
    );

const trackIterable = (
  eventType: string,
  eventId: string | number,
  product: IterableProduct,
): void => {
  try {
    if (
      typeof window !== 'undefined' &&
      //hacky temporary solution until we fix Iterable events locally
      window.location.host !== 'localhost:8080'
    ) {
      window.dataLayer.push({
        event: eventType,
        eventId: eventId,
        product,
      });
    }
  } catch (err) {
    logError((err as Error).message, {
      method: 'trackIterable',
    });
  }
};

const findAllByKey = (obj: any, keyToFind: string): any => {
  try {
    return (
      Object.entries(obj).reduce(
        (acc, [key, value]: any) =>
          key === keyToFind
            ? acc.concat(value)
            : typeof value === 'object' && value
            ? acc.concat(findAllByKey(value, keyToFind))
            : acc,
        [],
      ) || []
    );
  } catch (err) {
    logError((err as Error).message, {
      method: 'findAllByKey',
    });
  }
};

const getSkuKey = (
  item: Product | TestPlanItemAttribute | Variant,
  isVariant = false,
): string | void => {
  try {
    const memberShipTypeSubstr = item?.sku?.includes(
      MembershipTypeOptions.control,
    )
      ? 'control-membership-'
      : 'sti-membership-';
    const substr = !isVariant ? '-membership' : memberShipTypeSubstr;
    return item?.sku?.replace(substr, '') ?? '';
  } catch (err) {
    logError((err as Error).message, {
      method: 'getSkuKey',
    });
  }
};

const getVariantId = (memberShipCategory: MemberShipCategory, sku: string) => {
  try {
    const variant = findAllByKey(memberShipCategory, sku || '')[0] ?? '';
    const variantId = variant?.variantInfo
      ? variant?.variantInfo[sku]
      : variant;

    if (!variantId) throw new Error('Variant not found');

    return variantId;
  } catch (err) {
    logError((err as Error).message, {
      method: 'getVariantId',
    });
  }
};

export {
  capitalize,
  daysUntil,
  filterUniqueByCategory,
  findAllByKey,
  formatDate,
  formatForCurrency,
  formatPrice,
  generateKey,
  getMembershipTypeFromProduct,
  getSkuKey,
  getProductsBySlug,
  getVariantId,
  logError,
  loginUrl,
  membershipIntakeUrl,
  numericDate,
  scrollToId,
  snakeToTitle,
  timeAgo,
  trackIterable,
  truncateString,
  useClickOff,
  useDisableBodyScroll,
  useEffectAsyncWrapper,
  useHasScrollableContent,
};
